import React, { useCallback, useEffect, useRef, useState } from 'react';
import adminApi from '../api/client';

interface Seller {
  id: string;
  business_name: string;
  city: string;
  contact_phone: string;
  created_at: string;
  user_name: string;
  user_email: string;
  verification_status: string;
  is_suspended?: boolean; // FIX 10 (Aug 12, 2026)
  seller_kind?: 'personal' | 'business' | null;
  classification_available?: boolean;
}

interface AllSellersResponse {
  total: number;
  page: number;
  limit: number;
  sellers: Seller[];
}

const errorMessage = (error: any, fallback: string): string => {
  const detail = error.response?.data?.detail;
  return typeof detail === 'string' ? detail : typeof detail?.message === 'string' ? detail.message : fallback;
};

const Toast = ({ message, type, onClose }: { message: string, type: 'success' | 'error', onClose: () => void }) => {
  useEffect(() => {
    if (message) {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }
  }, [message, onClose]);
  
  if (!message) return null;
  return (
    <div className={`fixed bottom-4 right-4 px-6 py-3 rounded shadow-lg text-white font-medium z-50 ${type === 'success' ? 'bg-[#1D9E75]' : 'bg-[#D85A30]'}`}>
      {message}
    </div>
  );
};

const RejectModal = ({ isOpen, onClose, onConfirm, title, confirmLabel = "Confirm Reject", busy = false }: any) => {
  const [reason, setReason] = useState("");
  
  // reset reason on open
  useEffect(() => {
    if (isOpen) setReason("");
  }, [isOpen]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
        <h3 className="text-xl font-bold text-[#085041] mb-4">{title}</h3>
        <textarea
          className="w-full border border-gray-300 rounded p-3 mb-4 focus:outline-none focus:border-[#1D9E75] min-h-[120px] resize-none"
          aria-label="Reason for seller action"
          placeholder="Reason for this action..."
          maxLength={500}
          disabled={busy}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="flex justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded font-medium">Cancel</button>
          <button 
            onClick={() => onConfirm(reason)} 
            disabled={busy || !reason.trim()}
            className="px-4 py-2 bg-[#D85A30] text-white rounded hover:bg-opacity-90 disabled:opacity-50 font-medium"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  const normalized = status?.toLowerCase() || 'pending';
  let classes = "";
  if (normalized === 'pending') classes = "bg-[#BA7517]/10 text-[#BA7517]";
  else if (normalized === 'approved') classes = "bg-[#1D9E75]/10 text-[#1D9E75]";
  else if (normalized === 'rejected') classes = "bg-[#D85A30]/10 text-[#D85A30]";
  else classes = "bg-gray-100 text-gray-600";
  
  return (
    <span className={`px-2 py-1 rounded text-xs font-bold uppercase tracking-wide ${classes}`}>
      {normalized}
    </span>
  );
};

// FIX 10 (Aug 12, 2026): independent from verification_status - a seller
// can be approved AND suspended, so this renders alongside StatusBadge,
// not instead of it.
const SuspendedBadge = () => (
  <span className="px-2 py-1 rounded text-xs font-bold uppercase tracking-wide bg-red-100 text-red-700 ml-2">
    🚫 Suspended
  </span>
);

export default function Sellers() {
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending');
  
  const [pendingSellers, setPendingSellers] = useState<Seller[]>([]);
  const [allSellersData, setAllSellersData] = useState<AllSellersResponse>({ total: 0, page: 1, limit: 20, sellers: [] });
  const [page, setPage] = useState(1);
  const [kind, setKind] = useState('');
  const [busy, setBusy] = useState(false);
  const loadId = useRef(0);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({ show: false, message: '', type: 'success' });
  const [rejectModal, setRejectModal] = useState<{show: boolean, id: string | null}>({ show: false, id: null });
  const [suspendModal, setSuspendModal] = useState<{show: boolean, id: string | null}>({ show: false, id: null }); // FIX 10 (Aug 12, 2026)
  const [classifyModal, setClassifyModal] = useState<{show: boolean, id: string | null}>({ show: false, id: null });

  const fetchPending = useCallback(async () => {
    const id = ++loadId.current;
    try {
      setLoading(true);
      setError(null);
      setPendingSellers([]);
      const res = await adminApi.get(`/admin/sellers/pending?page=${page}&limit=20${kind ? `&seller_kind=${kind}` : ''}`);
      if (id === loadId.current) setPendingSellers(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      if (id === loadId.current) setError(errorMessage(err, 'Failed to fetch pending sellers'));
    } finally {
      if (id === loadId.current) setLoading(false);
    }
  }, [page, kind]);

  const fetchAll = useCallback(async (currentPage: number) => {
    const id = ++loadId.current;
    try {
      setLoading(true);
      setError(null);
      setAllSellersData({ total: 0, page: currentPage, limit: 20, sellers: [] });
      const res = await adminApi.get(`/admin/sellers/all?page=${currentPage}&limit=20${kind ? `&seller_kind=${kind}` : ''}`);
      if (id !== loadId.current) return;
      if (res.data?.sellers) {
        setAllSellersData(res.data);
      } else {
        // Fallback if structure mismatches slightly
        setAllSellersData({ total: 0, page: 1, limit: 20, sellers: Array.isArray(res.data) ? res.data : [] });
      }
    } catch (err: any) {
      if (id === loadId.current) setError(errorMessage(err, 'Failed to fetch all sellers'));
    } finally {
      if (id === loadId.current) setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPending();
    } else {
      fetchAll(page);
    }
  }, [activeTab, page, fetchPending, fetchAll]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ show: true, message, type });
  };

  const handleApprove = async (id: string) => {
    if (busy || !window.confirm('Approve this seller? Classification and suspension remain separate.')) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/sellers/${id}/approve`);
      showToast('Seller approved successfully', 'success');
      activeTab === 'pending' ? fetchPending() : fetchAll(page);
    } catch (err: any) {
      showToast(errorMessage(err, 'Failed to approve seller'), 'error');
    } finally { setBusy(false); }
  };

  const handleReject = async (reason: string) => {
    if (!rejectModal.id || busy) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/sellers/${rejectModal.id}/reject`, { reason });
      showToast('Seller rejected successfully', 'success');
      setRejectModal({ show: false, id: null });
      activeTab === 'pending' ? fetchPending() : fetchAll(page);
    } catch (err: any) {
      showToast(errorMessage(err, 'Failed to reject seller'), 'error');
    } finally { setBusy(false); }
  };

  // FIX 10 (Aug 12, 2026): suspend/unsuspend - same shape as approve/reject
  // above. Suspend requires a reason (reuses RejectModal); unsuspend does not.
  const handleSuspend = async (reason: string) => {
    if (!suspendModal.id || busy) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/sellers/${suspendModal.id}/suspend`, { reason });
      showToast('Seller suspended', 'success');
      setSuspendModal({ show: false, id: null });
      activeTab === 'pending' ? fetchPending() : fetchAll(page);
    } catch (err: any) {
      showToast(errorMessage(err, 'Failed to suspend seller'), 'error');
    } finally { setBusy(false); }
  };

  const handleUnsuspend = async (id: string) => {
    if (busy || !window.confirm('Unsuspend this seller? Eligible active listings may become visible again. Removed listings remain hidden.')) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/sellers/${id}/unsuspend`);
      showToast('Seller unsuspended', 'success');
      activeTab === 'pending' ? fetchPending() : fetchAll(page);
    } catch (err: any) {
      showToast(errorMessage(err, 'Failed to unsuspend seller'), 'error');
    } finally { setBusy(false); }
  };

  const handleClassify = async (reason: string) => {
    if (!classifyModal.id || busy || !window.confirm('Classify this legacy profile as business? Approval and suspension will remain unchanged. Type conversion requires separate review.')) return;
    setBusy(true);
    try {
      await adminApi.post(`/admin/sellers/${classifyModal.id}/classify`, { seller_kind: 'business', reason: reason.trim() });
      showToast('Seller classified as business', 'success');
      setClassifyModal({ show: false, id: null });
      activeTab === 'pending' ? fetchPending() : fetchAll(page);
    } catch (err: any) {
      showToast(errorMessage(err, 'Failed to classify seller'), 'error');
    } finally { setBusy(false); }
  };

  const renderTableData = () => {
    const list = activeTab === 'pending' ? pendingSellers : (allSellersData.sellers || []);
    
    if (loading && list.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-6 py-12 text-center text-gray-500">Loading sellers...</td>
        </tr>
      );
    }

    if (list.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
            No sellers found in this category.
          </td>
        </tr>
      );
    }

    return list.map((seller) => (
      <tr key={seller.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
        <td className="px-6 py-4 font-medium text-[#085041]">{seller.business_name}
          <span className="block text-xs text-gray-600">{seller.seller_kind === 'personal' ? 'Personal reader' : seller.seller_kind === 'business' ? 'Business' : 'Unclassified'}</span>
        </td>
        <td className="px-6 py-4">
          <div className="flex flex-col">
            <span className="text-gray-900">{seller.user_name}</span>
            <span className="text-xs text-gray-500">{seller.user_email}</span>
          </div>
        </td>
        <td className="px-6 py-4 text-gray-700">{seller.city}</td>
        <td className="px-6 py-4 text-gray-700 whitespace-nowrap">{seller.contact_phone}</td>
        <td className="px-6 py-4 whitespace-nowrap">
          <StatusBadge status={seller.verification_status} />
          {seller.is_suspended && <SuspendedBadge />}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right">
          {activeTab === 'pending' && (
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => handleApprove(seller.id)}
                disabled={busy || loading}
                className="px-3 py-1 bg-[#1D9E75] text-white text-sm rounded font-medium hover:bg-opacity-90"
              >
                ✓ Approve
              </button>
              <button
                onClick={() => setRejectModal({ show: true, id: seller.id })}
                disabled={busy || loading}
                className="px-3 py-1 bg-[#D85A30] text-white text-sm rounded font-medium hover:bg-opacity-90"
              >
                ✗ Reject
              </button>
            </div>
          )}
          {activeTab === 'all' && (
            // FIX 10 (Aug 12, 2026): suspend/unsuspend only shown on the
            // "All Sellers" tab - pending sellers can't act as sellers yet,
            // so suspending them is not a meaningful action.
            <div className="flex gap-2 justify-end">
              {seller.is_suspended ? (
                <button
                  onClick={() => handleUnsuspend(seller.id)}
                  disabled={busy || loading}
                  className="px-3 py-1 bg-[#1D9E75] text-white text-sm rounded font-medium hover:bg-opacity-90"
                >
                  ✓ Unsuspend
                </button>
              ) : (
                <button
                  onClick={() => setSuspendModal({ show: true, id: seller.id })}
                  disabled={busy || loading}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded font-medium hover:bg-opacity-90"
                >
                  🚫 Suspend
                </button>
              )}
            </div>
          )}
          {seller.classification_available && !seller.seller_kind && <button disabled={busy || loading} onClick={() => setClassifyModal({ show: true, id: seller.id })} className="text-sm underline mt-2">Classify as business</button>}
        </td>
      </tr>
    ));
  };

  return (
    <div className="bg-[#F1EFE8] min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-[#085041] mb-6">Seller Management</h1>
        
        {error && (
          <div className="bg-[#D85A30]/10 border border-[#D85A30] text-[#D85A30] px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button 
            className={`px-6 py-3 rounded-lg font-bold transition-colors ${activeTab === 'pending' ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => { setActiveTab('pending'); setPage(1); }} disabled={busy}
          >
            Pending Verification
          </button>
          <button 
            className={`px-6 py-3 rounded-lg font-bold transition-colors ${activeTab === 'all' ? 'bg-[#085041] text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            onClick={() => { setActiveTab('all'); setPage(1); }} disabled={busy}
          >
            All Sellers
          </button>
        </div>

        <label className="block mb-4">Seller type <select aria-label="Seller type" value={kind} disabled={busy} onChange={e => { setKind(e.target.value); setPage(1); }} className="border rounded p-2 ml-2">
          <option value="">All types</option><option value="personal">Personal readers</option><option value="business">Businesses</option><option value="unclassified">Unclassified</option>
        </select></label>
        <p className="text-sm text-gray-600 mb-4">Seller type, approval and suspension are independent. Personal profiles use contact listings; business contact selling requires a separate rollout.</p>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 text-sm uppercase tracking-wider">
                  <th className="px-6 py-4 font-bold">Seller / Type</th>
                  <th className="px-6 py-4 font-bold">Owner</th>
                  <th className="px-6 py-4 font-bold">City</th>
                  <th className="px-6 py-4 font-bold">Phone</th>
                  <th className="px-6 py-4 font-bold">Status</th>
                  <th className="px-6 py-4 font-bold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {renderTableData()}
              </tbody>
            </table>
          </div>
        </div>

        {activeTab === 'pending' && <div className="flex justify-center gap-2 mt-6">
          <button disabled={busy || loading || page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span>Page {page}</span>
          <button disabled={busy || loading || pendingSellers.length < 20} onClick={() => setPage(page + 1)}>Next</button>
        </div>}

        {activeTab === 'all' && allSellersData.total > 0 && Math.ceil((allSellersData.total ?? 0) / (allSellersData.limit || 20)) > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <button 
              disabled={busy || loading || page === 1}
              onClick={() => setPage(page - 1)}
              className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 bg-white border border-gray-200 rounded font-medium text-gray-700">
              Page {page} of {Math.ceil((allSellersData.total ?? 0) / (allSellersData.limit || 20))}
            </span>
            <button 
              disabled={busy || loading || page >= Math.ceil((allSellersData.total ?? 0) / (allSellersData.limit || 20))}
              onClick={() => setPage(page + 1)}
              className="px-4 py-2 bg-white border border-gray-200 rounded text-[#085041] disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <Toast 
        message={toast.show ? toast.message : ''} 
        type={toast.type} 
        onClose={() => setToast(prev => ({ ...prev, show: false }))} 
      />
      
      <RejectModal
        isOpen={rejectModal.show}
        onClose={() => setRejectModal({ show: false, id: null })}
        onConfirm={handleReject}
        title="Reject Seller Verification"
        busy={busy}
      />

      {/* FIX 10 (Aug 12, 2026): reuses the same reason-modal component as
          reject, just retitled - matches the codebase's own pattern instead
          of building a separate modal. */}
      <RejectModal
        isOpen={suspendModal.show}
        onClose={() => setSuspendModal({ show: false, id: null })}
        onConfirm={handleSuspend}
        title="Suspend Seller"
        confirmLabel="Confirm Suspend"
        busy={busy}
      />
      <RejectModal isOpen={classifyModal.show} onClose={() => setClassifyModal({ show: false, id: null })} onConfirm={handleClassify} title="Classify Legacy Seller as Business" confirmLabel="Confirm Business Classification" busy={busy} />
    </div>
  );
}
